import { buildApiUrl } from '../utils/apiConfig';

export interface CompanySummary {
    companyId: number;
    companyName: string;
    districtName: string | null;
    schoolName: string | null;
    type: string;
    subscriptionDate: string | null;
    subscriptionEnd: string | null;
    adminCount: number;
    staffCount: number;
    coachCount: number;
    athleteCount: number;
    planName: string | null;
    billingInterval: string | null;
    subscriptionStatus: string;
    instantChatEnabled?: boolean;
    instantChatReviewerUsernames?: string[];
}

export interface UserSummary {
    userId: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    role: string | null;
    companyName: string | null;
    districtName: string | null;
    schoolName: string | null;
    profileCreatedAt: string | null;
    lastLoginTime: string | null;
    status: string | null;
    instantChatEnabled?: boolean;
    companyInstantChatEnabled?: boolean | null;
}

export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

export interface LoginCountEntry {
    date: string;
    count: number;
}

export interface UsersByTypeEntry {
    profileType: string;
    count: number;
}

export interface SubscriptionsPerDayEntry {
    date: string;
    planName: string;
    count: number;
}

export interface LoginMetrics {
    entries: LoginCountEntry[];
}

export interface UsersByTypeMetrics {
    entries: UsersByTypeEntry[];
}

export interface SubscriptionsPerDayMetrics {
    entries: SubscriptionsPerDayEntry[];
}

const defaultHeaders = { 'Content-Type': 'application/json' };
const defaultOptions: RequestInit = { credentials: 'include', headers: defaultHeaders };

export interface PolarisCompanyFilters {
    companyName?: string;
    districtName?: string;
    schoolName?: string;
    type?: string;
    planName?: string;
    subscriptionStatus?: string;
}

export async function fetchCompanies(
    page = 0,
    size = 20,
    filters?: PolarisCompanyFilters,
): Promise<PageResponse<CompanySummary>> {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size),
    });
    if (filters) {
        (Object.entries(filters) as [keyof PolarisCompanyFilters, string | undefined][]).forEach(
            ([key, value]) => {
                const trimmed = value?.trim();
                if (trimmed) {
                    params.set(key, trimmed);
                }
            },
        );
    }
    const url = buildApiUrl(`api/polaris/companies?${params.toString()}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error("Unable to load companies. Please try again.");
    return res.json();
}

export interface PolarisUserFilters {
    name?: string;
    email?: string;
    role?: string;
    companyName?: string;
    districtName?: string;
    schoolName?: string;
    status?: string;
}

export async function fetchPolarisUsers(
    page = 0,
    size = 20,
    filters?: PolarisUserFilters,
): Promise<PageResponse<UserSummary>> {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size),
    });
    if (filters) {
        (Object.entries(filters) as [keyof PolarisUserFilters, string | undefined][]).forEach(
            ([key, value]) => {
                const trimmed = value?.trim();
                if (trimmed) {
                    params.set(key, trimmed);
                }
            },
        );
    }
    const url = buildApiUrl(`api/polaris/users?${params.toString()}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch users: ${res.status}`);
    return res.json();
}

export async function saveCompanyInstantChat(
    companyId: number,
    enabled: boolean,
    reviewerUsernames: string[] = [],
): Promise<CompanySummary> {
    const res = await fetch(buildApiUrl(`api/polaris/companies/${companyId}/instant-chat`), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify({ enabled, reviewerUsernames }),
    });
    if (!res.ok) throw new Error(await parsePolarisError(res, "Could not update Instant Chat for this organisation."));
    return res.json();
}

export async function saveUserInstantChat(
    userId: number,
    enabled: boolean,
): Promise<UserSummary> {
    const res = await fetch(buildApiUrl(`api/polaris/users/${userId}/instant-chat`), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify({ enabled }),
    });
    if (!res.ok) throw new Error(await parsePolarisError(res, "Could not update Instant Chat for this user."));
    return res.json();
}

export interface ResendWelcomeEmailResult {
    userId: number;
    email: string;
    message: string;
}

export async function resendWelcomeEmail(userId: number): Promise<ResendWelcomeEmailResult> {
    const res = await fetch(buildApiUrl(`api/polaris/users/${userId}/resend-welcome-email`), {
        ...defaultOptions,
        method: 'POST',
        body: '{}',
    });
    if (!res.ok) {
        let message = `Failed to resend welcome email: ${res.status}`;
        try {
            const data = await res.json();
            message = data.detail || data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface GenerateAccessLinkResult {
    userId: number;
    email: string;
    accessLink: string;
    expiresAt: string;
    message: string;
}

export async function generateAccessLink(userId: number): Promise<GenerateAccessLinkResult> {
    const res = await fetch(buildApiUrl(`api/polaris/users/${userId}/generate-access-link`), {
        ...defaultOptions,
        method: 'POST',
        body: '{}',
    });
    if (!res.ok) {
        let message = `Failed to generate access link: ${res.status}`;
        try {
            const data = await res.json();
            message = data.detail || data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface ResetMediaKitResult {
    userId: number;
    email: string;
    message: string;
}

export interface PolarisCapabilities {
    mediaKitResetEnabled: boolean;
    shortBillingIntervalsEnabled: boolean;
}

export async function fetchPolarisCapabilities(): Promise<PolarisCapabilities> {
    const res = await fetch(buildApiUrl('api/polaris/capabilities'), defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch polaris capabilities: ${res.status}`);
    return res.json();
}

export async function resetAthleteMediaKit(userId: number): Promise<ResetMediaKitResult> {
    const res = await fetch(buildApiUrl(`api/polaris/users/${userId}/media-kit/reset`), {
        ...defaultOptions,
        method: 'POST',
        body: '{}',
    });
    if (!res.ok) {
        let message = `Failed to reset media kit: ${res.status}`;
        try {
            const data = await res.json();
            message = data.detail || data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface ResetOnboardingWizardResult {
    userId: number;
    email: string;
    message: string;
}

export async function resetAthleteOnboardingWizard(userId: number): Promise<ResetOnboardingWizardResult> {
    const res = await fetch(buildApiUrl(`api/polaris/users/${userId}/onboarding-wizard/reset`), {
        ...defaultOptions,
        method: 'POST',
        body: '{}',
    });
    if (!res.ok) {
        let message = `Failed to reset onboarding wizard: ${res.status}`;
        try {
            const data = await res.json();
            message = data.detail || data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export async function fetchLoginMetrics(from?: string, to?: string): Promise<LoginMetrics> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const url = buildApiUrl(`api/polaris/metrics/logins?${params.toString()}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch login metrics: ${res.status}`);
    return res.json();
}

export async function fetchUsersByType(): Promise<UsersByTypeMetrics> {
    const url = buildApiUrl('api/polaris/metrics/users-by-type');
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch users by type: ${res.status}`);
    return res.json();
}

export async function fetchSubscriptionsPerDay(): Promise<SubscriptionsPerDayMetrics> {
    const url = buildApiUrl('api/polaris/metrics/subscriptions-per-day');
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch subscriptions per day: ${res.status}`);
    return res.json();
}

export interface ContactNotificationSummary {
    demoRequestCount: number;
    salesRequestCount: number;
    familyPlanRequestCount: number;
}

export interface ContactNotificationRow {
    id?: number;
    sentAt: string;
    requesterEmail: string;
    formType: string;
}

export interface CollegeDataIngestionStatus {
    running: boolean;
    startedAt: string | null;
    completedAt: string | null;
    collegesUpserted: number | null;
    sportsUpserted: number | null;
    rowsSkipped: number | null;
    error: string | null;
}

export interface CollegeDataIngestionSummary {
    totalColleges: number;
    totalSports: number;
    latestCollegeUpdatedAt: string | null;
    latestSportsUpdatedAt: string | null;
}

export async function fetchContactNotificationSummary(from?: string, to?: string): Promise<ContactNotificationSummary> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const q = params.toString();
    const url = buildApiUrl(`api/polaris/sales/contact-notifications/summary${q ? `?${q}` : ''}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch contact notification summary: ${res.status}`);
    return res.json();
}

export async function fetchContactNotificationsPage(
    from?: string,
    to?: string,
    page = 0,
    size = 20
): Promise<PageResponse<ContactNotificationRow>> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('page', String(page));
    params.set('size', String(size));
    const url = buildApiUrl(`api/polaris/sales/contact-notifications?${params.toString()}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch contact notifications: ${res.status}`);
    return res.json();
}

export async function fetchCollegeDataIngestionStatus(): Promise<CollegeDataIngestionStatus> {
    const url = buildApiUrl("api/polaris/college-data/status");
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch college ingestion status: ${res.status}`);
    return res.json();
}

export async function fetchCollegeDataIngestionSummary(): Promise<CollegeDataIngestionSummary> {
    const url = buildApiUrl("api/polaris/college-data/summary");
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch college ingestion summary: ${res.status}`);
    return res.json();
}

export async function triggerCollegeDataIngestion(): Promise<CollegeDataIngestionStatus> {
    const url = buildApiUrl("api/polaris/college-data/ingest");
    const res = await fetch(url, { ...defaultOptions, method: "POST", body: "{}" });
    if (!res.ok) throw new Error(`Failed to trigger college ingestion: ${res.status}`);
    return res.json();
}

export interface ContentEventRow {
    userId: number | null;
    name: string | null;
    email: string | null;
    companyName: string | null;
    contentType: string | null;
    eventType: string;
    occurredAt: string | null;
}

export interface ModuleInsight {
    moduleId: number | null;
    title: string | null;
    contentType: string | null;
    uniqueUsers: number;
    totalEvents: number;
    events: ContentEventRow[];
}

export interface CourseInsightSummary {
    courseId: number;
    tab: number;
    tabLabel: string;
    title: string;
    moduleCount: number;
    uniqueUsers: number;
    totalEvents: number;
    lastActivity: string | null;
}

export interface CourseInsightDetail {
    courseId: number;
    tab: number;
    tabLabel: string;
    title: string;
    modules: ModuleInsight[];
}

export async function fetchCourseInsights(
    page = 0,
    size = 20,
    tab?: number,
    search?: string
): Promise<PageResponse<CourseInsightSummary>> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(size));
    if (tab != null) params.set("tab", String(tab));
    if (search) params.set("search", search);
    const url = buildApiUrl(`api/polaris/course-insights?${params.toString()}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch course insights: ${res.status}`);
    return res.json();
}

export async function fetchCourseInsightDetail(courseId: number, tab: number): Promise<CourseInsightDetail> {
    const url = buildApiUrl(`api/polaris/course-insights/${courseId}?tab=${tab}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch course insight detail: ${res.status}`);
    return res.json();
}

export interface InterestAdminItem {
    id: number;
    name: string;
    active: boolean;
    displayOrder: number;
}

export interface InterestCategoryAdminItem {
    id: number;
    name: string;
    displayOrder: number;
    interests: InterestAdminItem[];
}

export async function fetchPolarisInterestCatalog(): Promise<InterestCategoryAdminItem[]> {
    const res = await fetch(buildApiUrl('api/polaris/reference-data/interests'), defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch interest catalog: ${res.status}`);
    return res.json();
}

export async function createPolarisInterest(categoryId: number, name: string): Promise<InterestAdminItem> {
    const res = await fetch(buildApiUrl('api/polaris/reference-data/interests'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ categoryId, name }),
    });
    if (!res.ok) {
        let message = `Failed to create interest: ${res.status}`;
        try {
            const data = await res.json();
            message = data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export async function updatePolarisInterest(
    interestId: number,
    payload: { name?: string; active?: boolean }
): Promise<InterestAdminItem> {
    const res = await fetch(buildApiUrl(`api/polaris/reference-data/interests/${interestId}`), {
        ...defaultOptions,
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        let message = `Failed to update interest: ${res.status}`;
        try {
            const data = await res.json();
            message = data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface SportAdminItem {
    id: number;
    name: string;
    active: boolean;
    sortOrder: number;
    category?: string;
}

export interface SportCategoryAdminItem {
    name: string;
    sports: SportAdminItem[];
}

export async function fetchPolarisSportCatalog(): Promise<SportCategoryAdminItem[]> {
    const res = await fetch(buildApiUrl('api/polaris/reference-data/sports'), defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch sport catalog: ${res.status}`);
    return res.json();
}

export async function createPolarisSport(category: string, name: string): Promise<SportAdminItem> {
    const res = await fetch(buildApiUrl('api/polaris/reference-data/sports'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ category, name }),
    });
    if (!res.ok) {
        let message = `Failed to create sport: ${res.status}`;
        try {
            const data = await res.json();
            message = data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export async function updatePolarisSport(
    sportId: number,
    payload: { name?: string; active?: boolean; category?: string }
): Promise<SportAdminItem> {
    const res = await fetch(buildApiUrl(`api/polaris/reference-data/sports/${sportId}`), {
        ...defaultOptions,
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        let message = `Failed to update sport: ${res.status}`;
        try {
            const data = await res.json();
            message = data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface CacheInfo {
    name: string;
    tier: string;
    ttlDescription: string;
    description: string;
}

export interface EvictCacheResult {
    cacheName: string;
    evictedAt: string;
}

export async function fetchPolarisCaches(): Promise<CacheInfo[]> {
    const url = buildApiUrl('api/polaris/caches');
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(`Failed to fetch caches: ${res.status}`);
    return res.json();
}

export async function evictPolarisCache(cacheName: string): Promise<EvictCacheResult> {
    const url = buildApiUrl(`api/polaris/caches/${encodeURIComponent(cacheName)}/evict`);
    const res = await fetch(url, { ...defaultOptions, method: 'POST', body: '{}' });
    if (!res.ok) {
        let message = `Failed to bust cache: ${res.status}`;
        try {
            const data = await res.json();
            message = data.detail || data.message || message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export interface CleverLinkFields {
    cleverId?: string;
    cleverDistrictId?: string;
    cleverDistrictName?: string;
    cleverSchoolId?: string;
    cleverSchoolName?: string;
    enteredSchoolName?: string;
}

export interface ProvisionOrganizationRequest extends CleverLinkFields {
    organizationName: string;
    adminEmail: string;
    adminFirstName?: string;
    adminLastName?: string;
    amountCents: number;
    currency: string;
    billingInterval: 'month' | 'year' | 'day';
    intervalCount?: number;
    maxAthletes: number;
    metadata?: Record<string, string>;
    trialPeriodDays?: number | null;
    /** Active Stripe promotion code applied to admin checkout. */
    promotionCode?: string | null;
    /** When false, suppresses the checkout invite email to the customer. Defaults to sending. */
    sendCheckoutInviteEmail?: boolean;
    sourceNotificationId?: number | null;
    notes?: string;
}

export interface AttachOrganizationSubscriptionRequest extends CleverLinkFields {
    organizationName: string;
    adminEmail: string;
    adminFirstName?: string;
    adminLastName?: string;
    stripeSubscriptionId: string;
    maxAthletesOverride?: number | null;
    metadata?: Record<string, string>;
    sourceNotificationId?: number | null;
    notes?: string;
}

export interface ProvisionOrganizationResponse {
    opportunityId: number;
    companyId: number;
    adminUserId: number;
    organizationName: string;
    adminEmail: string;
    status: string;
    stripeProductId: string | null;
    stripePriceId: string | null;
    stripeSubscriptionId: string | null;
    checkoutUrl: string | null;
    amountCents: number | null;
    currency: string | null;
    billingInterval: string | null;
    intervalCount: number | null;
    maxAthletes: number | null;
    metadata: Record<string, string>;
    createdAt: string;
    checkoutInviteEmailSent?: boolean | null;
    promotionCode?: string | null;
}

async function parsePolarisError(res: Response, fallback: string): Promise<string> {
    try {
        const data = await res.json();
        return data.detail || data.message || fallback;
    } catch {
        return fallback;
    }
}

export async function provisionOrganization(
    body: ProvisionOrganizationRequest,
): Promise<ProvisionOrganizationResponse> {
    const res = await fetch(buildApiUrl('api/polaris/organizations/provision'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        throw new Error(await parsePolarisError(res, `Failed to provision organization: ${res.status}`));
    }
    return res.json();
}

export async function attachOrganizationSubscription(
    body: AttachOrganizationSubscriptionRequest,
): Promise<ProvisionOrganizationResponse> {
    const res = await fetch(buildApiUrl('api/polaris/organizations/attach-subscription'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        throw new Error(await parsePolarisError(res, `Failed to attach subscription: ${res.status}`));
    }
    return res.json();
}

export async function fetchOrganizationOpportunities(
    page = 0,
    size = 20,
): Promise<PageResponse<ProvisionOrganizationResponse>> {
    const res = await fetch(
        buildApiUrl(`api/polaris/organizations/opportunities?page=${page}&size=${size}`),
        defaultOptions,
    );
    if (!res.ok) {
        throw new Error(`Failed to fetch opportunities: ${res.status}`);
    }
    return res.json();
}

export interface RagUsageLimits {
    maxUserMessageTokens: number;
    maxOutputTokens: number;
    maxContextTokens: number;
    dailyTokenLimit: number;
    monthlyTokenLimit: number;
    dailyRequestLimit: number;
}

export interface PlatformAiSettings extends RagUsageLimits {
    inputUsdPerMillion: number;
    outputUsdPerMillion: number;
}

export interface UsageSummary {
    period: string;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    requestCount: number;
    costUsd: number;
}

export interface MyAssistUsage {
    canSubmit: boolean;
    blockedReason: string | null;
    message: string | null;
    remainingDailyTokens: number;
    remainingMonthlyTokens: number;
    remainingDailyRequests: number;
    maxUserMessageTokens: number;
    dailyUsedTokens: number;
    assistAvailable?: boolean;
}

export interface CompanyLimitOverrides {
    maxUserMessageTokens: number | null;
    maxOutputTokens: number | null;
    maxContextTokens: number | null;
    dailyTokenLimit: number | null;
    monthlyTokenLimit: number | null;
    dailyRequestLimit: number | null;
}

export interface CompanySettingsView {
    companyId: number;
    companyName: string;
    effective: RagUsageLimits;
    platform: RagUsageLimits;
    overrides: CompanyLimitOverrides;
    assistHidden: boolean;
    assistEvaluatorUsernames: string[];
    webSearchEnabled: boolean;
    allowedDomains: string[];
    webSearchUsesPlatformDefaults: boolean;
}

export interface CompanyWebSearchUpdate {
    inheritPlatform?: boolean;
    webSearchEnabled?: boolean;
    allowedDomains?: string[];
}

export interface CompanyAssistAccessUpdate {
    hidden: boolean;
    evaluatorUsernames: string[];
}

export interface AssistAccess {
    available: boolean;
}

export interface CompanyAiUsage {
    settings: CompanySettingsView;
    period: UsageSummary;
    lifetime: UsageSummary;
}

export interface UserAssistStatus {
    canSubmit: boolean;
    blockedReason: string | null;
    message: string | null;
    remainingDailyTokens: number;
    remainingMonthlyTokens: number;
    remainingDailyRequests: number;
    dailyRequests: number;
    dailyTotalTokens: number;
    dailyInputTokens: number;
    dailyOutputTokens: number;
    monthlyTotalTokens: number;
    monthlyInputTokens: number;
    monthlyOutputTokens: number;
}

export interface UserAiUsage {
    userId: number;
    displayName: string;
    email: string;
    current: UserAssistStatus;
    lifetimeTokens: number;
    lifetimeCostUsd: number;
    effectiveLimits: RagUsageLimits;
}

export interface ConversationUsageRow {
    sessionId: string;
    title: string;
    updatedAt: string;
    createdAt: string;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
}

export interface ConversationMessageRow {
    role: string;
    content: string;
    createdAt: string;
    inputTokens: number;
    outputTokens: number;
}

async function parseAssistError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { detail?: string; message?: string };
        return body.detail ?? body.message ?? fallback;
    } catch {
        return fallback;
    }
}

export interface AssistCollegePageItem {
    name: string;
    city?: string | null;
    state?: string | null;
}

export interface AssistCollegePage {
    colleges: AssistCollegePageItem[];
    offset: number;
    limit: number;
    total: number;
}

export async function fetchAssistCollegePage(
    message: string,
    priorUserMessage: string | undefined,
    offset: number,
    limit: number,
    excludeNames: string[] = [],
): Promise<AssistCollegePage> {
    const res = await fetch(buildApiUrl("api/rag/colleges/page"), {
        ...defaultOptions,
        method: "POST",
        body: JSON.stringify({
            message,
            priorUserMessage: priorUserMessage ?? null,
            offset,
            limit,
            excludeNames,
        }),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load more colleges"));
    return res.json();
}

export async function fetchAssistAccess(): Promise<AssistAccess> {
    const res = await fetch(buildApiUrl("api/rag/access"), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load Assist access"));
    return res.json();
}

export async function fetchMyAssistUsage(): Promise<MyAssistUsage> {
    const res = await fetch(buildApiUrl("api/rag/usage/me"), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load usage"));
    return res.json();
}

export async function fetchAssistUsageSummary(period: "daily" | "monthly" | "yearly"): Promise<UsageSummary> {
    const res = await fetch(buildApiUrl(`api/rag/admin/usage/summary?period=${period}`), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load usage summary"));
    return res.json();
}

export async function fetchAssistPlatformSettings(): Promise<PlatformAiSettings> {
    const res = await fetch(buildApiUrl("api/rag/admin/usage/settings"), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load settings"));
    return res.json();
}

export async function saveAssistPlatformSettings(body: PlatformAiSettings): Promise<PlatformAiSettings> {
    const res = await fetch(buildApiUrl("api/rag/admin/usage/settings"), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not save settings"));
    return res.json();
}

export interface AssistRuntimeSettings {
    chunkSize: number;
    chunkOverlap: number;
    topKResults: number;
    similarityThreshold: number;
    embeddingBatchSize: number;
    embeddingMaxRetries: number;
    embeddingInterBatchDelayMs: number;
    guardrailsEnabled: boolean;
    topicClassificationEnabled: boolean;
    maxMessageLength: number;
    athleteHourlyLimit: number;
    athleteDailyLimit: number;
    staffHourlyLimit: number;
    staffDailyLimit: number;
    externalDataEnabled: boolean;
    webSearchEnabled: boolean;
    webSearchMaxUses: number;
    webSearchModel: string;
    webSearchToolType: string;
    allowedDomains: string[];
}

export async function fetchAssistRuntimeSettings(): Promise<AssistRuntimeSettings> {
    const res = await fetch(buildApiUrl("api/rag/admin/runtime"), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load Assist configuration"));
    return res.json();
}

export async function saveAssistRuntimeSettings(
    body: AssistRuntimeSettings,
): Promise<AssistRuntimeSettings> {
    const res = await fetch(buildApiUrl("api/rag/admin/runtime"), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not save Assist configuration"));
    return res.json();
}

export async function fetchCompanyAiUsage(
    companyId: number,
    period: "daily" | "monthly" | "yearly",
): Promise<CompanyAiUsage> {
    const res = await fetch(
        buildApiUrl(`api/rag/admin/usage/companies/${companyId}?period=${period}`),
        defaultOptions,
    );
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load company usage"));
    return res.json();
}

export async function saveCompanyAiLimits(
    companyId: number,
    overrides: CompanyLimitOverrides,
): Promise<CompanySettingsView> {
    const res = await fetch(buildApiUrl(`api/rag/admin/usage/companies/${companyId}`), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify(overrides),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not save company limits"));
    return res.json();
}

export async function saveCompanyAssistAccess(
    companyId: number,
    body: CompanyAssistAccessUpdate,
): Promise<CompanySettingsView> {
    const res = await fetch(buildApiUrl(`api/rag/admin/usage/companies/${companyId}/assist-access`), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not save Assist access"));
    return res.json();
}

export async function saveCompanyWebSearch(
    companyId: number,
    body: CompanyWebSearchUpdate,
): Promise<CompanySettingsView> {
    const res = await fetch(buildApiUrl(`api/rag/admin/usage/companies/${companyId}/web-search`), {
        ...defaultOptions,
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not save web search settings"));
    return res.json();
}

export async function fetchUserAiUsage(userId: number): Promise<UserAiUsage> {
    const res = await fetch(buildApiUrl(`api/rag/admin/usage/users/${userId}`), defaultOptions);
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load user usage"));
    return res.json();
}

export async function fetchUserAiConversations(
    userId: number,
    params: {
        page?: number;
        size?: number;
        from?: string;
        to?: string;
        minTokens?: string;
        maxTokens?: string;
        minCostUsd?: string;
        maxCostUsd?: string;
    },
): Promise<PageResponse<ConversationUsageRow>> {
    const search = new URLSearchParams({
        page: String(params.page ?? 0),
        size: String(params.size ?? 10),
    });
    if (params.from) search.set("from", params.from);
    if (params.to) search.set("to", params.to);
    if (params.minTokens) search.set("minTokens", params.minTokens);
    if (params.maxTokens) search.set("maxTokens", params.maxTokens);
    if (params.minCostUsd) search.set("minCostUsd", params.minCostUsd);
    if (params.maxCostUsd) search.set("maxCostUsd", params.maxCostUsd);
    const res = await fetch(
        buildApiUrl(`api/rag/admin/usage/users/${userId}/conversations?${search}`),
        defaultOptions,
    );
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load conversations"));
    return res.json();
}

export async function fetchUserAiConversation(
    userId: number,
    sessionId: string,
): Promise<ConversationMessageRow[]> {
    const res = await fetch(
        buildApiUrl(`api/rag/admin/usage/users/${userId}/conversations/${sessionId}`),
        defaultOptions,
    );
    if (!res.ok) throw new Error(await parseAssistError(res, "Could not load conversation"));
    return res.json();
}

export interface CollegeFinderResultItem {
    ipedsId: string | null;
    name: string | null;
    city: string | null;
    state: string | null;
    division: string | null;
    size: number | null;
    campusSetting: string | null;
}

export interface CollegeFinderSearchItem {
    id: number;
    searchedAt: string;
    sports: string[];
    divisions: string[];
    states: string[];
    minSize: number | null;
    maxSize: number | null;
    campusSettings: string[];
    schoolType: string | null;
    resultCount: number;
    results: CollegeFinderResultItem[];
    userMessage?: string | null;
}

export interface CollegeFinderSearchHistory {
    userId: number;
    displayName: string;
    email: string;
    page: number;
    totalPages: number;
    totalSearches: number;
    searches: CollegeFinderSearchItem[];
    chatPage: number;
    chatTotalPages: number;
    chatTotalSearches: number;
    chatSearches: CollegeFinderSearchItem[];
}

export async function fetchUserCollegeSearches(
    userId: number,
    page = 0,
    chatPage = 0,
): Promise<CollegeFinderSearchHistory> {
    const res = await fetch(
        buildApiUrl(
            `api/polaris/users/${userId}/college-searches?page=${page}&chatPage=${chatPage}&size=10`,
        ),
        defaultOptions,
    );
    if (!res.ok) {
        if (res.status === 404) {
            throw new Error("That user could not be found.");
        }
        throw new Error("Could not load college searches.");
    }
    return res.json();
}
